import { Flex, Space } from "antd";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getOrderOpenedCheckoutListByPointsThunk,
    getPointInfoCollection,
    getPriceListByPoint,
    getStorageInventoryGet,
} from "tm-shared/api";
import { displayDateFormat } from "tm-shared/constants";
import { OrderType } from "tm-shared/types";
import { arrayToRecord } from "tm-shared/utils";

import { getPointStorageId } from "@/domains/point";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { BaseModal } from "@/shared/ui/BaseModal";
import { SecondaryText } from "@/shared/ui/Typography";
import { getUniqueDefinedValues } from "@/utils";

import { CheckoutCard } from "./CheckoutCard/CheckoutCard";
import {
    PointToPricesMap,
    PointToStorageMap,
    ProductRemainingsMap,
    StorageToRemainingsMap,
} from "./YesterdayOpenedCheckouts.types";

interface YesterdayOpenedCheckoutsModalProps {
    onCloseModal: () => void;
}

const orderTypeVocabulary = {
    [OrderType.H]: "Продаж",
    [OrderType.R]: "Повернення",
    [OrderType.P]: "Промо",
};

export const YesterdayOpenedCheckoutsModal: FC<
    YesterdayOpenedCheckoutsModalProps
> = ({ onCloseModal }) => {
    const dispatch = useAppDispatch();

    const route = useAppSelector((s) => s.user.route);
    const yesterdayCheckoutList = useAppSelector(
        (s) => s.work.yesterdayCheckoutList,
    );
    const offlineMode = useOfflineModeSelector();

    const yesterdayLabel = dayjs()
        .subtract(1, "day")
        .format(displayDateFormat.date);

    const [submitting, setSubmitting] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [errorsPointIds, setErrorsPointIds] = useState<string[]>([]);

    const [pointToStorageMap, setPointToStorageMap] =
        useState<PointToStorageMap>({});

    const [storageToRemainingsMap, setStorageToRemainingsMap] =
        useState<StorageToRemainingsMap>({});

    const [prices, setPrices] = useState<PointToPricesMap>({});

    const uniquePointIds = useMemo(
        () =>
            getUniqueDefinedValues(
                yesterdayCheckoutList.map((item) => item.pointId),
            ),
        [yesterdayCheckoutList],
    );

    useEffect(() => {
        const loadRemainings = async () => {
            if (!uniquePointIds.length) {
                setPointToStorageMap({});
                setStorageToRemainingsMap({});
                return;
            }

            setInitialLoading(true);

            try {
                const { result: points } = await getPointInfoCollection({
                    ids: uniquePointIds,
                });

                const nextPointToStorageMap: PointToStorageMap = {};

                for (const point of points) {
                    const pointId = point.id;
                    const storageId = getPointStorageId(point);

                    if (!pointId || !storageId) continue;

                    nextPointToStorageMap[pointId] = storageId;
                }

                const uniqueStorageIds = Array.from(
                    new Set(Object.values(nextPointToStorageMap)),
                );

                if (!uniqueStorageIds.length) {
                    setPointToStorageMap(nextPointToStorageMap);
                    setStorageToRemainingsMap({});
                    return;
                }

                const remainingsResponses = await Promise.all(
                    uniqueStorageIds.map(async (storageId) => {
                        try {
                            const { result } = await getStorageInventoryGet({
                                storageId,
                            });

                            return {
                                storageId,
                                result,
                            };
                        } catch {
                            const relatedPointIds = Object.entries(
                                nextPointToStorageMap,
                            )
                                .filter(
                                    ([, currentStorageId]) =>
                                        currentStorageId === storageId,
                                )
                                .map(([pointId]) => pointId);

                            setErrorsPointIds((prev) =>
                                getUniqueDefinedValues([
                                    ...prev,
                                    ...relatedPointIds,
                                ]),
                            );

                            return null;
                        }
                    }),
                );

                const nextStorageToRemainingsMap: StorageToRemainingsMap = {};

                for (const response of remainingsResponses) {
                    if (!response) continue;

                    nextStorageToRemainingsMap[response.storageId] =
                        response.result;
                }

                setPointToStorageMap(nextPointToStorageMap);
                setStorageToRemainingsMap(nextStorageToRemainingsMap);
            } finally {
                setInitialLoading(false);
            }
        };

        const loadPrices = async () => {
            await Promise.allSettled(
                uniquePointIds.map(async (pointId) => {
                    try {
                        const { result: pointPrices } =
                            await getPriceListByPoint({ pointId });

                        const pricesMap = arrayToRecord(pointPrices, "barcode");

                        setPrices((prev) => ({
                            ...prev,
                            [pointId]: pricesMap,
                        }));
                    } catch {
                        setErrorsPointIds((prev) =>
                            getUniqueDefinedValues([...prev, pointId]),
                        );
                    }
                }),
            );
        };

        setErrorsPointIds([]);
        void loadRemainings();
        void loadPrices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getRemainingsByPointId = (pointId: string): ProductRemainingsMap => {
        const storageId = pointToStorageMap[pointId];

        if (!storageId) return {};

        return storageToRemainingsMap[storageId] ?? {};
    };

    const handleCloseModal = useCallback(() => {
        if (!offlineMode) {
            dispatch(
                getOrderOpenedCheckoutListByPointsThunk({
                    routeId: route?.uuid,
                }),
            );
        }

        onCloseModal();
    }, [dispatch, offlineMode, onCloseModal, route?.uuid]);

    useEffect(() => {
        if (offlineMode) {
            onCloseModal();
        }
    }, [offlineMode, onCloseModal]);

    const isClosingRef = useRef(false);

    useEffect(() => {
        if (isClosingRef.current) return;

        if (yesterdayCheckoutList.length === 0) {
            isClosingRef.current = true;
            handleCloseModal();
        }
    }, [handleCloseModal, yesterdayCheckoutList.length]);

    const isModalClosable = useMemo(() => {
        return yesterdayCheckoutList.every(({ pointId }) =>
            errorsPointIds.includes(pointId),
        );
    }, [errorsPointIds, yesterdayCheckoutList]);

    const onAfterMove = useCallback(
        async (pointId: string) => {
            const storageId = pointToStorageMap[pointId];

            if (!storageId) return;

            try {
                setInitialLoading(true);
                const { result: updatedInventory } =
                    await getStorageInventoryGet({
                        storageId,
                    });

                setStorageToRemainingsMap((prev) => ({
                    ...prev,
                    [storageId]: updatedInventory,
                }));
            } catch (error) {
                console.error(error);

                setErrorsPointIds((prev) => [...prev, pointId]);
            } finally {
                setInitialLoading(false);
            }
        },
        [pointToStorageMap],
    );

    return (
        <BaseModal
            open
            onClose={handleCloseModal}
            title={`Незакриті замовлення за ${yesterdayLabel}`}
            confirmText="Зберегти"
            actions={null}
            mask={{ closable: false }}
            closable={isModalClosable}
            titleStyle={{ fontSize: 18 }}
        >
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                <SecondaryText>
                    Для кожного замовлення виберіть дію: видалити або перенести
                    на сьогодні.
                </SecondaryText>

                <Flex gap={16}>
                    <SecondaryText>
                        Замовлень: {yesterdayCheckoutList.length}
                    </SecondaryText>
                </Flex>

                <Flex vertical gap={12}>
                    {yesterdayCheckoutList.map((item) => (
                        <CheckoutCard
                            key={item.id}
                            checkout={item}
                            orderTypeVocabulary={orderTypeVocabulary}
                            submitting={submitting}
                            setSubmitting={setSubmitting}
                            remainings={getRemainingsByPointId(item.pointId)}
                            prices={prices[item.pointId] ?? {}}
                            hasLoadError={errorsPointIds.includes(item.pointId)}
                            initialLoading={initialLoading}
                            onAfterMove={onAfterMove}
                        />
                    ))}
                </Flex>
            </Space>
        </BaseModal>
    );
};
